import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react"

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatNumber(value, maxDecimals, minDecimals = maxDecimals) {
  if (value === "" || value === null || value === undefined || isNaN(value))
    return ""
  return value.toLocaleString("es-AR", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  })
}

function parseFormattedNumber(str) {
  const cleaned = str.replace(/\./g, "").replace(",", ".")
  const n = parseFloat(cleaned)
  return isNaN(n) ? NaN : n
}

function parseToFlat(raw, nroDecimales) {
  let out = ""
  let hasComma = false

  for (const ch of raw) {
    if (ch === "-" && out.length === 0) {
      out += ch
    } else if (ch === "," && nroDecimales > 0 && !hasComma) {
      hasComma = true
      out += ","
    } else if (ch === ".") {
      continue
    } else if (/[0-9]/.test(ch)) {
      if (hasComma) {
        const dec = out.slice(out.indexOf(",") + 1)
        if (dec.length >= nroDecimales) continue
      }
      out += ch
    }
  }
  return out
}

function applyThousands(flat) {
  const neg = flat.startsWith("-")
  const body = neg ? flat.slice(1) : flat
  const sep = body.indexOf(",")
  const intPart = sep === -1 ? body : body.slice(0, sep)
  const decPart = sep === -1 ? "" : body.slice(sep + 1)
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  const result = sep !== -1 ? `${grouped},${decPart}` : grouped
  return neg ? `-${result}` : result
}

function displayFormatted(flat, nroDecimales) {
  let result = applyThousands(flat)
  if (nroDecimales > 0 && flat.includes(",")) {
    const idx = result.indexOf(",")
    const dec = result.slice(idx + 1)
    if (dec.length < nroDecimales) {
      result = result.slice(0, idx + 1) + dec.padEnd(nroDecimales, "0")
    }
  }
  return result
}

// ── Componente ────────────────────────────────────────────────────────────────

/**
 * Input numérico con formato es-AR (miles=".", decimal=",").
 *
 * value   : number | ""   (sin formato)
 * onChange : (value: number | "") => void
 */
const NumberInput = ({
  value,
  onChange,
  nroDecimales = 0,
  minDecimals,
  min,
  max,
  placeholder,
  required,
  disabled,
  id,
  name,
  className,
  style,
  "aria-label": ariaLabel,
}) => {
  const effectiveMinDecimals =
    minDecimals !== undefined ? minDecimals : nroDecimales

  const [displayValue, setDisplayValue] = useState(() => {
    const v =
      value !== "" && value !== null && value !== undefined ? Number(value) : ""
    return v !== ""
      ? displayFormatted(
          parseToFlat(
            formatNumber(v, nroDecimales, effectiveMinDecimals),
            nroDecimales,
          ),
          nroDecimales,
        )
      : ""
  })
  const inputRef = useRef(null)
  const caretTargetRef = useRef(null)

  // Sincroniza cuando value cambia externamente (fuera de foco)
  useEffect(() => {
    if (inputRef.current !== document.activeElement) {
      const v =
        value !== "" && value !== null && value !== undefined
          ? Number(value)
          : ""
      const next =
        v !== ""
          ? displayFormatted(
              parseToFlat(
                formatNumber(v, nroDecimales, effectiveMinDecimals),
                nroDecimales,
              ),
              nroDecimales,
            )
          : ""
      setDisplayValue(next)
    }
  }, [value, nroDecimales, effectiveMinDecimals])

  // Posiciona el cursor tras el commit de React
  useLayoutEffect(() => {
    const node = inputRef.current
    if (!node || document.activeElement !== node) return
    const target = caretTargetRef.current
    if (target === null) return
    caretTargetRef.current = null
    node.setSelectionRange(target, target)
  }, [displayValue])

  // Convierte "." (teclado US) a "," al escribir
  const handleKeyDown = useCallback(
    e => {
      if (e.key === "." && nroDecimales > 0) {
        e.preventDefault()
        const node = inputRef.current
        if (!node) return
        const start = node.selectionStart ?? node.value.length
        const end = node.selectionEnd ?? start
        const next = node.value.slice(0, start) + "," + node.value.slice(end)
        const setter = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          "value",
        ).set
        setter.call(node, next)
        node.dispatchEvent(new Event("input", { bubbles: true }))
        node.setSelectionRange(start + 1, start + 1)
      }
    },
    [nroDecimales],
  )

  function handleChange(e) {
    const raw = e.currentTarget.value
    const flat = parseToFlat(raw, nroDecimales)
    const masked = displayFormatted(flat, nroDecimales)
    setDisplayValue(masked)

    const rawCursor = e.currentTarget.selectionStart ?? raw.length
    let cleanPos = 0
    let hasComma = false
    let decCount = 0
    for (let i = 0; i < Math.min(rawCursor, raw.length); i++) {
      const c = raw[i]
      if (c === "-" && cleanPos === 0) cleanPos++
      else if (c === "," && nroDecimales > 0 && !hasComma) {
        hasComma = true
        cleanPos++
      } else if (c === ".") continue
      else if (/[0-9]/.test(c)) {
        if (hasComma) {
          if (decCount >= nroDecimales) continue
          decCount++
        }
        cleanPos++
      }
    }
    caretTargetRef.current = applyThousands(flat.slice(0, cleanPos)).length
  }

  function handleBlur() {
    caretTargetRef.current = null
    const parsed = parseFormattedNumber(displayValue)

    if (Number.isFinite(parsed)) {
      let v = parsed
      if (min !== undefined && v < min) v = min
      if (max !== undefined && v > max) v = max
      const rounded = Number(v.toFixed(nroDecimales))
      setDisplayValue(formatNumber(rounded, nroDecimales, effectiveMinDecimals))
      onChange(rounded)
    } else if (displayValue.trim() === "") {
      setDisplayValue("")
      onChange("")
    } else {
      const v =
        value !== "" && value !== null && value !== undefined
          ? Number(value)
          : ""
      setDisplayValue(
        v !== "" ? formatNumber(v, nroDecimales, effectiveMinDecimals) : "",
      )
    }
  }

  const numericValue =
    displayValue.trim() !== "" ? parseFormattedNumber(displayValue) : undefined

  return (
    <input
      ref={inputRef}
      id={id}
      name={name}
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      aria-valuenow={Number.isFinite(numericValue) ? numericValue : undefined}
      aria-valuemin={min}
      aria-valuemax={max}
      value={displayValue}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder ?? (nroDecimales > 0 ? "0,00" : "0")}
      required={required}
      disabled={disabled}
      autoComplete="off"
      style={{ textAlign: "right", ...style }}
      className={`form-control${className ? ` ${className}` : ""}`}
    />
  )
}

export default NumberInput
